/**
 * Specialized hooks for Request operations
 *
 * @module @shared/services/hooks/useRequestsApi
 */

import { useCallback } from 'react';
import { useApiCore } from './useApiCore';
import type {
    EmployeeRequest,
    OrgEmployeeRequest,
    PutAvailabilityPattern,
    EmployeeRequestsList,
    OrgEmployeeRequestsList,
} from '@shared/models/requestTypes';
import type { ApiResult } from '../apiService';

/**
 * Hook that provides Employee Request-specific API methods with automatic authentication.
 *
 * @returns Object with Employee Request methods
 *
 * @example
 * ```typescript
 * const { get, getAll, getOpen, update, addComment, submit } = useEmployeeRequestsApi();
 *
 * const request = await get(employeeId, requestId);
 * const allRequests = await getAll(employeeId);
 * ```
 */
export function useEmployeeRequestsApi(): {
    get: (employeeId: string, requestId: string) => ApiResult<EmployeeRequest>;
    getAll: (employeeId: string) => ApiResult<EmployeeRequestsList>;
    getOpen: (employeeId: string) => ApiResult<EmployeeRequestsList>;
    getClosed: (employeeId: string) => ApiResult<EmployeeRequestsList>;
    update: (
        employeeId: string,
        requestId: string,
        pattern: PutAvailabilityPattern,
    ) => ApiResult<EmployeeRequest>;
    addComment: (
        employeeId: string,
        requestId: string,
        comment: string,
    ) => ApiResult<EmployeeRequest>;
    submit: (
        employeeId: string,
        requestId: string,
    ) => ApiResult<EmployeeRequest>;
} {
    const { getToken, apiService } = useApiCore();

    return {
        get: useCallback(
            async (
                employeeId: string,
                requestId: string,
            ): ApiResult<EmployeeRequest> => {
                const token = await getToken();
                return apiService.requests.employee.get(
                    employeeId,
                    requestId,
                    token,
                );
            },
            [getToken, apiService],
        ),

        getAll: useCallback(
            async (employeeId: string): ApiResult<EmployeeRequestsList> => {
                const token = await getToken();
                return apiService.requests.employee.getAll(employeeId, token);
            },
            [getToken, apiService],
        ),

        getOpen: useCallback(
            async (employeeId: string): ApiResult<EmployeeRequestsList> => {
                const token = await getToken();
                return apiService.requests.employee.getOpen(employeeId, token);
            },
            [getToken, apiService],
        ),

        getClosed: useCallback(
            async (employeeId: string): ApiResult<EmployeeRequestsList> => {
                const token = await getToken();
                return apiService.requests.employee.getClosed(
                    employeeId,
                    token,
                );
            },
            [getToken, apiService],
        ),

        update: useCallback(
            async (
                employeeId: string,
                requestId: string,
                pattern: PutAvailabilityPattern,
            ): ApiResult<EmployeeRequest> => {
                const token = await getToken();
                return apiService.requests.employee.update(
                    employeeId,
                    requestId,
                    pattern,
                    token,
                );
            },
            [getToken, apiService],
        ),

        addComment: useCallback(
            async (
                employeeId: string,
                requestId: string,
                comment: string,
            ): ApiResult<EmployeeRequest> => {
                const token = await getToken();
                return apiService.requests.employee.addComment(
                    employeeId,
                    requestId,
                    comment,
                    token,
                );
            },
            [getToken, apiService],
        ),

        submit: useCallback(
            async (
                employeeId: string,
                requestId: string,
            ): ApiResult<EmployeeRequest> => {
                const token = await getToken();
                return apiService.requests.employee.submit(
                    employeeId,
                    requestId,
                    token,
                );
            },
            [getToken, apiService],
        ),
    };
}

/**
 * Hook that provides OrgEmployee Request-specific API methods with automatic authentication.
 *
 * @returns Object with OrgEmployee Request methods
 *
 * @example
 * ```typescript
 * const { get, getAll, getOpen, update, addComment, submit } = useOrgEmployeeRequestsApi();
 *
 * const request = await get(orgEmployeeId, requestId);
 * const allRequests = await getAll(orgEmployeeId);
 * ```
 */
export function useOrgEmployeeRequestsApi(): {
    get: (
        orgEmployeeId: string,
        requestId: string,
    ) => ApiResult<OrgEmployeeRequest>;
    getAll: (orgEmployeeId: string) => ApiResult<OrgEmployeeRequestsList>;
    getOpen: (orgEmployeeId: string) => ApiResult<OrgEmployeeRequestsList>;
    getClosed: (orgEmployeeId: string) => ApiResult<OrgEmployeeRequestsList>;
    update: (
        orgEmployeeId: string,
        requestId: string,
        pattern: PutAvailabilityPattern,
    ) => ApiResult<OrgEmployeeRequest>;
    addComment: (
        orgEmployeeId: string,
        requestId: string,
        comment: string,
    ) => ApiResult<OrgEmployeeRequest>;
    submit: (
        orgEmployeeId: string,
        requestId: string,
    ) => ApiResult<OrgEmployeeRequest>;
} {
    const { getToken, apiService } = useApiCore();

    return {
        get: useCallback(
            async (
                orgEmployeeId: string,
                requestId: string,
            ): ApiResult<OrgEmployeeRequest> => {
                const token = await getToken();
                return apiService.requests.orgEmployee.get(
                    orgEmployeeId,
                    requestId,
                    token,
                );
            },
            [getToken, apiService],
        ),

        getAll: useCallback(
            async (
                orgEmployeeId: string,
            ): ApiResult<OrgEmployeeRequestsList> => {
                const token = await getToken();
                return apiService.requests.orgEmployee.getAll(
                    orgEmployeeId,
                    token,
                );
            },
            [getToken, apiService],
        ),

        getOpen: useCallback(
            async (
                orgEmployeeId: string,
            ): ApiResult<OrgEmployeeRequestsList> => {
                const token = await getToken();
                return apiService.requests.orgEmployee.getOpen(
                    orgEmployeeId,
                    token,
                );
            },
            [getToken, apiService],
        ),

        getClosed: useCallback(
            async (
                orgEmployeeId: string,
            ): ApiResult<OrgEmployeeRequestsList> => {
                const token = await getToken();
                return apiService.requests.orgEmployee.getClosed(
                    orgEmployeeId,
                    token,
                );
            },
            [getToken, apiService],
        ),

        update: useCallback(
            async (
                orgEmployeeId: string,
                requestId: string,
                pattern: PutAvailabilityPattern,
            ): ApiResult<OrgEmployeeRequest> => {
                const token = await getToken();
                return apiService.requests.orgEmployee.update(
                    orgEmployeeId,
                    requestId,
                    pattern,
                    token,
                );
            },
            [getToken, apiService],
        ),

        addComment: useCallback(
            async (
                orgEmployeeId: string,
                requestId: string,
                comment: string,
            ): ApiResult<OrgEmployeeRequest> => {
                const token = await getToken();
                return apiService.requests.orgEmployee.addComment(
                    orgEmployeeId,
                    requestId,
                    comment,
                    token,
                );
            },
            [getToken, apiService],
        ),

        submit: useCallback(
            async (
                orgEmployeeId: string,
                requestId: string,
            ): ApiResult<OrgEmployeeRequest> => {
                const token = await getToken();
                return apiService.requests.orgEmployee.submit(
                    orgEmployeeId,
                    requestId,
                    token,
                );
            },
            [getToken, apiService],
        ),
    };
}
